import { ClassificationService } from 'src/app/topic-classification/classification.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, lastValueFrom } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';

import { ClientFilterObject } from '@common/filter.decorator';

import { C } from 'src/app/@shared/constants';
import { ContentOptions, ContentService } from 'src/app/@core/content.service';
import { ProtoUrl } from 'src/app/@shared/protoUrl';
import { UserService } from 'src/app/user/@shared/user.service';

import { TopicModel, TopicSource } from 'src/app/topic/@shared/topic.model';

@Injectable({
  providedIn: 'root',
})
export class TopicService extends ContentService<TopicModel, TopicSource> {
  private path = 'topics';

  constructor(private http: HttpClient,
    private userService: UserService,
    classifications: ClassificationService) {
    super();
    // Only renames change the titles projected into existing topics.
    classifications.changed.subscribe(({ type }) => {
      if (type !== 'rename') { return; }
      for (const [id, stream] of Object.entries(this.items)) {
        if (!stream) { continue; }
        if (!stream.observed) {
          stream.next(null);
          continue;
        }
        const metadata = this.metadataStore[id];
        this.loadContent(id, {
          type: metadata?.appliedFilter ? 'list' : 'detail',
          filter: metadata?.appliedFilter ? { ...metadata.appliedFilter, skip: 0 } : undefined,
        }).subscribe({ error: () => { stream.next(null); } });
      }
    });
  }

  public loadContent(id: string, options: ContentOptions<TopicSource> = {}): Observable<TopicModel[] | null> {
    if (!options.type) {
      console.warn('[TopicService] loadContent(id, options) no type provided -> defaults to loadListContent');
    }

    if (options.type !== 'detail') {
      return this.loadListContent(id, options);
    }

    return this.loadDetailContent(id, options).pipe(map((item: TopicModel | null) => {
      return item
        ? [item]
        : null;
    }));
  }

  public create(attributes: Partial<TopicSource>, storeId: string): Promise<TopicModel> {
    return lastValueFrom(this.http.post<TopicSource>(ProtoUrl.getUrl(this.path), attributes)
      .pipe(map((item: TopicSource) => { return new TopicModel(item); }),
        tap((item: TopicModel) => {
          if (this.metadataStore[storeId]) {
            this.metadataStore[storeId].totalCount += 1;
          }

          this.updateItem(item, storeId, true);
        })));
  }

  public patch(id: string, attributes: Partial<TopicSource>): Promise<TopicModel> {
    return this.http.patch<TopicSource>(ProtoUrl.getUrl(`${this.path}/${id}`), attributes)
      .pipe(map((item: TopicSource) => { return new TopicModel(item); }),
        tap((item: TopicModel) => {
          this.updateItem(item);
        }))
      .toPromise();
  }

  public delete(id: string): Promise<boolean> {
    return this.http.delete<TopicSource>(ProtoUrl.getUrl(`${this.path}/${id}`))
      .pipe(map(() => {
        return true;
      }),
      tap(() => {
        this.removeItem(id);
      }))
      .toPromise();
  }

  protected loadListContent(id: string, options: ContentOptions<TopicSource>): Observable<TopicModel[]> {
    const filter: ClientFilterObject<TopicSource> = this.getNormalizedFilter(id, options.filter);
    const url = ProtoUrl.getUrl(this.path, {
      filter,
    });

    console.info('[TopicService] load items', url);
    let totalCount: number;

    return this.http.get<TopicSource[]>(url, {
      observe: 'response',
    })
      .pipe(
        timeout(C.DEFAULT_TIMEOUT),
        tap((response: HttpResponse<TopicSource[]>) => {
          const { headers } = response;

          totalCount = parseInt(headers.get('x-total-count')) || (response.body?.length || 0);
        }),
        map((response: HttpResponse<TopicSource[]>) => { return response.body || []; }),
        map((items: TopicSource[]) => { return items.map((item) => { return new TopicModel(item); }); }),
        tap((items: TopicModel[]) => {
          return this.setItems(items, id, filter, totalCount);
        }),
      );
  }

  protected loadDetailContent(id: string, options: ContentOptions<TopicSource>): Observable<TopicModel> {
    const { filter } = options;
    const url = ProtoUrl.getUrl(`${this.path}/${id}`, {
      filter,
    });

    return this.http.get<TopicSource>(url)
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((item) => {
          return item
            ? new TopicModel(item)
            : null;
        }),
        tap((item) => {
          this.updateItem(item);
        }));
  }
}
